import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { getDatabase, ref, onValue, off } from 'firebase/database';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';

import HeroBackground from './HeroBackground';
import HeroPropertyInfo from './HeroPropertyInfo';
import HeroThumbnailStrip from './HeroThumbnailStrip';
import HeroActionButtons from './HeroActionButtons';
import GalleryModal from './GalleryModal';
import BidCenter from '../../BidCenter/BidCenter';
import { buildAnalyticsLabel } from './heroUtils';

import { inititaliseFirebase } from '@/components/common/FirebaseApp';
import { getLatestBidCenter, getPDPPrint } from '@/api/PDP';
import { onSavePress, onUnsavePress } from '@/api/srpFilter';
import { getautobidAmount } from '@/api/bidDepositApiHold';
import {
  setAuctionDetails,
  setRedirectionAction,
  updateSaveProperty,
} from '@/redux/PDP/pdpSlice';
import { setLoginModal, setLoginTab, setLoginCallLocation, setShowSessionMsg, setIsUserSubscribed } from '@/redux/login/loginSlice';
import { globalCmsData } from '@/redux/cms/cmsSelectors';
import { trackPageMount, trackButtonClick, trackApiCall } from '@/utils/analytics';

import './commercial-hero.css';

/**
 * CommercialHeroSection - Container component for the Commercial PDP Hero Section.
 *
 * Renders the real BidCenter component (same logic as residential PDP)
 * inside the commercial hero layout with updated styling.
 */
const CommercialHeroSection = ({ pdpCmsData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const pdpInitData = useSelector((state) => state.pdp.pdpInitData);
  const latestBidCenterData = useSelector((state) => state.pdp.latestBidCenterData);
  const bidCenterData = useSelector((state) => state.pdp.bidCenterData);
  const auctionDetails = useSelector((state) => state.pdp.auctionDetails);
  const isUserLoggedIn = useSelector((state) => state.user.isUserLoggedIn);
  const pdpGlobalCmsData = useSelector(globalCmsData);

  // Local state
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [cmsData, setCmsData] = useState(pdpCmsData);
  const [showTransactionFeeModal, setShowTransactionFeeModal] = useState(false);
  const [transactionFeeData, setTransactionFeeData] = useState(null);
  const [bidCenterLoaded, setBidCenterLoaded] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Mark bid center as loaded when data arrives (fresh for this page load)
  useEffect(() => {
    if (bidCenterData && Object.keys(bidCenterData).length > 0) {
      setBidCenterLoaded(true);
    }
  }, [bidCenterData]);

  // Fallback: show BidCenter after 2s even if data hasn't loaded (handles unavailable listings)
  useEffect(() => {
    const timer = setTimeout(() => {
      setBidCenterLoaded(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Firebase listener ref for cleanup
  const firebaseListenerRef = useRef(null);

  // Derived data from Redux
  const propertyDetails = pdpInitData?.propertyDetails;
  const propertyId = propertyDetails?.propertyId;
  const listingId = propertyDetails?.listingId;
  const address = propertyDetails?.address;
  const commonAttributes = propertyDetails?.commonAttributes;
  const detailedAttributes = propertyDetails?.detailedAttributes;
  const listingDetail = pdpInitData?.listingDetail;
  const businessListingId = listingDetail?.businessListingId;
  const propertySpecialDetails = pdpInitData?.propertySpecialDetails;
  const commercialFeatures = propertySpecialDetails?.commercialPropertyAdditionalFeatures;
  const onWatchList = propertySpecialDetails?.onWatchList === 'Y';
  const propCategory = commonAttributes?.propCategory;
  const status = commonAttributes?.status;
  const listingType = listingDetail?.listingType;
  const financialType = listingDetail?.financialType;
  const offerType = pdpInitData?.offerCenterDetail?.offerTypes?.[0];
  const offerCenterDetail = pdpInitData?.offerCenterDetail;
  const isFcOnlineFlag = pdpInitData?.fcOnlineFlag;
  const isAllowedPreSale = detailedAttributes?.isAllowedPreSale;
  const hasHnb = offerCenterDetail?.hasHnB;
  const isWatchProperty = offerType === 'WATCH_PROPERTY';

  // Building name from commercialPropertyAdditionalFeatures (displayed in hero)
  const buildingName = commercialFeatures?.otherBuildingName || commonAttributes?.propertyName;

  // Property images - source from imageDetails (API response) with fallback to propertyImages
  const imageDetails = propertyDetails?.imageDetails || [];
  const propertyImages = propertySpecialDetails?.propertyImages || [];
  const images = imageDetails.length > 0
    ? [...imageDetails]
        .sort((a, b) => (a.imageDisplaySequence || 0) - (b.imageDisplaySequence || 0))
        .map((img) => ({
          url: img?.pdpBigImage || img?.imageStoragePath,
          thumbnail: img?.thumbnailUrl || img?.pdpSmallImage,
          alt: img?.imageName || 'Property image',
          imageTitle: img?.imageTitle || '',
          isDefault: img?.defaultImage === true,
        }))
    : propertyImages.map((img) => ({
        url: img?.imageUrl,
        thumbnail: img?.imageUrl,
        alt: img?.imageDescription || 'Property image',
        imageTitle: img?.imageTitle || '',
        isDefault: false,
      }));

  // Current background image - use the default image if available, otherwise first image
  const defaultImage = images.find((img) => img.isDefault);
  const currentImageUrl = defaultImage?.url || images[0]?.url || null;

  // Video data from virtualTourDetailsList
  const virtualTourList = propertyDetails?.virtualTourDetailsList || [];
  const videoData = virtualTourList
    .filter((v) => v?.virtualTourUrl)
    .map((v) => {
      const match = v.virtualTourUrl.match(/src=["']?([^\s"']+)/);
      return match ? match[1] : v.virtualTourUrl;
    })
    .filter(Boolean);

  // ─── Compute statusPayload (same as residential PDP) ──────────────────────
  const statusPayload = useMemo(() => {
    const isPreview = isAllowedPreSale;
    const isUnderReviewAuction = status === 'REVIEW' && listingType === 'AUCN';
    const isUnderContractAuction = status === 'SPND' && listingType === 'AUCN';
    const isForeclosure = propCategory === 'FORECLOSURE';
    const isActiveAuction = status === 'ACTV' && listingType === 'AUCN';
    const isShortSaleUnderContractAuction = propCategory === 'SHRT_SALE' && status === 'SPND' && listingType === 'AUCN';
    const isShortSaleUnderContractTraditional = propCategory === 'SHRT_SALE' && listingType === 'TRNL' && offerCenterDetail?.offerTypes?.indexOf('PLACE_BACKUP_OFFER') > -1;
    const isUnderContractTraditional = offerCenterDetail?.offerTypes?.indexOf('PLACE_BACKUP_OFFER') > -1;
    const isActive = status === 'ACTV';
    const isTraditional = listingType === 'TRNL';
    const isUpcoming = status === 'WFA' || status === 'PIFA';
    const isUnderReview = status === 'REVIEW';
    const isShortSaleUnderReview = propCategory === 'SHRT_SALE' && status === 'REVIEW' && listingType === 'AUCN';
    const isHNBTraditional = isTraditional && hasHnb === 'Y';
    const isPact = status === 'PACT';
    const hnbDeadlnDt = offerCenterDetail?.hnbDeadlineDt || null;

    return {
      isActive,
      isActiveAuction,
      isUnderReview,
      isTraditional,
      isPreview,
      isUpcoming,
      isUnderReviewAuction,
      isUnderContractAuction,
      isForeclosure,
      isUnderContractTraditional,
      isHNBTraditional,
      isShortSaleUnderContractAuction,
      isShortSaleUnderContractTraditional,
      isShortSaleUnderReview,
      isPact,
      hasHnb,
      hnbDeadlnDt,
    };
  }, [status, listingType, propCategory, offerCenterDetail, isAllowedPreSale, hasHnb]);

    const {
    buyerAgentCommissionPercent,
    buyerAgentCommissionAmount,

  } = propertySpecialDetails || {};

  const getFormatPrice = (amnt) => {
    if (amnt?.length === 0 || isNaN(amnt)) return "";
    return amnt?.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
  };

   const buyerAgentCommissionText =
    bidCenterData?.buyerPremiumPercentage > 0  ? bidCenterData?.buyerPremiumPercentage + '%' :
         bidCenterData?.buyerPremiumAmount !== 0 &&
            bidCenterData?.buyerPremiumAmount !== null
          ? "$" + getFormatPrice(bidCenterData?.buyerPremiumAmount)
          : "No"

  // Sync CMS data from parent prop
  useEffect(() => {
    if (pdpCmsData) {
      setCmsData(pdpCmsData);
    }
  }, [pdpCmsData]);

  // Firebase environment detection
  const getFirebaseEnv = () => {
    const host = window.location.host;
    if (host === 'preview.hubzu.com') return 'UAT';
    if (host === 'hubzu.com' || host === 'www.hubzu.com') return 'PROD';
    return 'INTEG';
  };

  // ─── Firebase Listener Setup ───────────────────────────────────────────────
  useEffect(() => {
    if (!businessListingId || !pdpGlobalCmsData?.firebaseConfig) return;

    const env = getFirebaseEnv();
    const fbConfig = pdpGlobalCmsData.firebaseConfig[env];

    if (!fbConfig) return;

    inititaliseFirebase(fbConfig);

    const db = getDatabase();
    const listingPath = `/${fbConfig.dbPath}listings/${businessListingId}`;
    const listingDetailsRef = ref(db, listingPath);

    firebaseListenerRef.current = listingDetailsRef;

    onValue(listingDetailsRef, (snapshot) => {
      const data = snapshot.val();
      if (data?.auctionDetails?.auctionTimeLeft) {
        pollLatestBidCenter();
      }
    });

    return () => {
      if (firebaseListenerRef.current) {
        off(firebaseListenerRef.current);
        firebaseListenerRef.current = null;
      }
    };
  }, [businessListingId, pdpGlobalCmsData]);

  // ─── Analytics: Track Page Mount ───────────────────────────────────────────
  useEffect(() => {
    if (!propertyId || !listingId || !address) return;

    const eventLabel = buildAnalyticsLabel(propertyId, listingId, address);
    if (!eventLabel) return;

    trackPageMount({
      pageName: 'commercial_pdp_hero_loaded',
      eventCategory: 'COMMERCIAL-PDP',
      eventSource: 'on page mount',
      eventLabel,
    });
  }, [propertyId, listingId]);

  // ─── Poll Latest Bid Center ────────────────────────────────────────────────
  const pollLatestBidCenter = async () => {
    if (!listingId) return;
    try {
      const details = await getLatestBidCenter(listingId);
      if (details) {
        dispatch(setAuctionDetails(details));
      }
    } catch (error) {
      console.error('Error polling latest bid center:', error);
    }
  };

  // ─── Handle Save Action ────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isUserLoggedIn) {
      dispatch(setRedirectionAction('handleSave'));
      dispatch(setLoginModal(true));
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      if (onWatchList) {
        dispatch(updateSaveProperty('N'));
        const response = await onUnsavePress({ propertyId });
        if (response === 'success') {
          trackApiCall({
            eventName: 'favourite_property_unsaved',
            eventCategory: 'COMMERCIAL-PDP',
            eventSource: 'pdp_commercial_hero',
            eventLabel: `Property Id: ${propertyId}`,
          });
        } else {
          dispatch(updateSaveProperty('Y'));
          toast.error('Failed to remove property from saved list.');
          trackApiCall({
            eventName: 'favourite_property_unsave_error',
            eventCategory: 'Error',
            eventSource: `Error - ${response?.message || 'Data Initialization Failed'}`,
            eventLabel: `Property Id: ${propertyId}`,
          });
        }
      } else {
        dispatch(updateSaveProperty('Y'));
        const response = await onSavePress({ propertyId });
        if (response === 'success') {
          trackApiCall({
            eventName: 'favourite_property_saved',
            eventCategory: 'COMMERCIAL-PDP',
            eventSource: 'pdp_commercial_hero',
            eventLabel: `Property Id: ${propertyId}`,
          });
        } else {
          dispatch(updateSaveProperty('N'));
          toast.error('Failed to save property.');
          trackApiCall({
            eventName: 'favourite_property_save_error',
            eventCategory: 'Error',
            eventSource: `Error - ${response?.message || 'Data Initialization Failed'}`,
            eventLabel: `Property Id: ${propertyId}`,
          });
        }
      }
    } catch (error) {
      dispatch(updateSaveProperty(onWatchList ? 'Y' : 'N'));
      toast.error('Save operation failed. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Handle Print Action ───────────────────────────────────────────────────
  const handlePrint = async () => {
    if (isPrinting) return;
    setIsPrinting(true);

    try {
      await getPDPPrint({ propertyId });
    } catch (error) {
      toast.error('PDF could not be generated. Please try again.');
    } finally {
      setIsPrinting(false);
    }
  };

  // ─── Handle Register to Bid (opens login popup with registration tab) ─────
  const handleRegisterToBid = () => {
    dispatch(setRedirectionAction('handlePlaceBid'));
    dispatch(setLoginTab('loginTab'));
    dispatch(setLoginCallLocation('Popup'));
    dispatch(setShowSessionMsg(false));
    dispatch(setIsUserSubscribed(true));
    dispatch(setLoginModal(true));
  };

  // ─── Handle Transaction Fee Modal Open ─────────────────────────────────────
  const handleTransactionFeeOpen = async () => {
    setShowTransactionFeeModal(true);
    const nextBidAmount = auctionDetails?.nextpossibleBidAmount || latestBidCenterData?.nextpossibleBidAmount || bidCenterData?.nextpossibleBidAmount;
    if (businessListingId && nextBidAmount) {
      try {
        const response = await getautobidAmount(businessListingId, nextBidAmount);
        if (response?.data?.data) {
          setTransactionFeeData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching transaction fee details:', error);
      }
    }
  };

  // ─── Handle Thumbnail Click ────────────────────────────────────────────────
  const handleThumbnailClick = (index) => {
    // Open the gallery modal at the clicked image index
    setSelectedImageIndex(index);
    setShowGalleryModal(true);

    const eventLabel = buildAnalyticsLabel(propertyId, listingId, address);
    if (eventLabel) {
      trackButtonClick({
        eventName: 'gallery_interaction',
        eventCategory: 'COMMERCIAL-PDP',
        eventSource: 'Thumbnail Click',
        eventLabel,
      });
    }
  };

  // ─── Handle See All Click ──────────────────────────────────────────────────
  const handleSeeAllClick = () => {
    const eventLabel = buildAnalyticsLabel(propertyId, listingId, address);
    if (eventLabel) {
      trackButtonClick({
        eventName: 'gallery_interaction',
        eventCategory: 'COMMERCIAL-PDP',
        eventSource: 'See All Click',
        eventLabel,
      });
    }
  };

  // ─── Handle Image Error ────────────────────────────────────────────────────
  const handleImageError = () => {
    setImageLoadError(true);
  };

  // ─── Alt text for background ───────────────────────────────────────────────
  const altText = address
    ? `Exterior of property at ${address.streetNumber} ${address.streetName}, ${address.city}, ${address.state}`
    : 'Commercial property exterior';

  return (
    <section className="commercial-hero" id="Overview">
      <div className="commercial-hero__container">
        {/* Background Image */}
        <HeroBackground
          imageUrl={currentImageUrl}
          altText={altText}
          onImageError={handleImageError}
          hasError={imageLoadError}
        />

        {/* Content Overlay */}
        <div className="commercial-hero__content">
          {/* Left: Property Info + Thumbnails + Actions */}
          <div className="commercial-hero__left">
            <HeroPropertyInfo
              categoryLabel={(() => {
                // Use propTypeDisplay (title case) if available, else type with underscores replaced
                const rawType = commonAttributes?.propTypeDisplay || commonAttributes?.type || '';
                const displayType = rawType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
                const subType = commonAttributes?.subType || '';
                // Don't append subType if it's "other" (case-insensitive)
                const isOther = subType.toLowerCase() === 'other';

                if (!isOther && subType.toLowerCase().includes('office')) {
                  return commercialFeatures?.officeClass
                    ? `CLASS ${commercialFeatures.officeClass} ${displayType} ${subType}`.trim()
                    : `${displayType} ${subType}`.trim();
                }
                if (isOther) {
                  return commercialFeatures?.otherBuildingClass
                    ? `CLASS ${commercialFeatures.otherBuildingClass} ${displayType}`.trim()
                    : `${displayType}`.trim();
                }
                return commercialFeatures?.otherBuildingClass
                  ? `CLASS ${commercialFeatures.otherBuildingClass} ${displayType} ${subType}`.trim()
                  : `${displayType} ${subType || commonAttributes?.propCategory || ''}`.trim();
              })()}
              propertyName={buildingName}
              address={address}
              squareFeet={detailedAttributes?.squareFeet || commercialFeatures?.officeFloorPlates}
              tenantCount={
                commercialFeatures?.retailTenantRoster || commercialFeatures?.retailNumberOfUnits || detailedAttributes?.tenantCount || null
              }
              occupancyPercentage={detailedAttributes?.occupancyPercentage || commercialFeatures?.occupancyPercentage}
              isLand={commonAttributes?.type?.toLowerCase() === 'land' || commonAttributes?.propTypeDisplay?.toLowerCase() === 'land'}
              lotSize={commonAttributes?.lotSize}
              lotSizeUnit={commonAttributes?.lotSizeUnit}
            />
            <div className="commercial-hero__bottom-left">
              {images.length > 0 && (
                <HeroThumbnailStrip
                  images={images}
                  videoData={videoData}
                  selectedIndex={selectedImageIndex}
                  onThumbnailClick={handleThumbnailClick}
                  onSeeAllClick={handleSeeAllClick}
                  totalImageCount={images.length}
                />
              )}
            </div>
          </div>

          {/* Right: BidCenter (same component as residential PDP, styled differently) */}
          <div className="commercial-hero__right commercial-hero__right--mobile-bidcard">
            <div className={`commercial-hero__bid-card commercial-mobile-bidcard${pdpInitData?.cifCompleted && statusPayload.isPact ? ' commercial-hero__bid-card--cif-complete' : ''}`}>
              {pdpInitData && (Object.keys(bidCenterData).length > 0 || bidCenterLoaded) && cmsData ? (
                <BidCenter
                  statusPayload={statusPayload}
                  pdpBidCenter={cmsData?.pdpBidCenter || {}}
                  pdpData={pdpInitData}
                  financialType={financialType}
                  listingType={listingType}
                  status={status}
                  offerType={offerType}
                  offerCenterDetail={offerCenterDetail}
                  isFcOnlineFlag={isFcOnlineFlag}
                  pdpPropertyFees={cmsData?.pdpPropertyFees}
                  isWatchProperty={isWatchProperty}
                  caFcSalePropertyDetails={null}
                  isCAFCSaleProperty={false}
                  onSaveModalChange={setShowSaveModal}
                />
              ) : (
                <div className="commercial-hero__bid-card--loading">
                  <div className="shimmer-line shimmer-line--short" />
                  <div className="shimmer-line shimmer-line--timer" />
                  <div className="shimmer-line shimmer-line--medium" />
                  <div className="shimmer-line shimmer-line--amount" />
                  <div className="shimmer-line shimmer-line--short" />
                  <div className="shimmer-line shimmer-line--button" />
                </div>
              )}
              {/* Footer: Transaction Fee + Register to Bid */}
              <div className="commercial-hero__bid-card-footer">
                {buyerAgentCommissionText && buyerAgentCommissionText?.length > 0 && listingType !== null  && (
                  <button type="button" className="commercial-hero__bid-card-footer-link" onClick={() => buyerAgentCommissionText !== 'No' ? handleTransactionFeeOpen() : ""}>
                    {buyerAgentCommissionText} Transaction Fee
                  </button>
                )}
                {!isUserLoggedIn && statusPayload.isActiveAuction && (
                  <button
                    type="button"
                    className="commercial-hero__bid-card-footer-link"
                    onClick={handleRegisterToBid}
                  >
                    Register to Bid
                  </button>
                )}
              </div>
              {/* Mobile: Action buttons inside bid card */}
              <div className="commercial-hero__bid-card-mobile-actions d-md-none">
                <HeroActionButtons
                  propertyId={propertyId}
                  listingId={listingId}
                  isUserLoggedIn={isUserLoggedIn}
                  isSaved={onWatchList}
                  address={address}
                  onSave={handleSave}
                  onPrint={handlePrint}
                />
              </div>
            </div>
            <div className="commercial-hero__bottom-right">
              <HeroActionButtons
                propertyId={propertyId}
                listingId={listingId}
                isUserLoggedIn={isUserLoggedIn}
                isSaved={onWatchList}
                address={address}
                onSave={handleSave}
                onPrint={handlePrint}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      <GalleryModal
        show={showGalleryModal}
        onHide={() => setShowGalleryModal(false)}
        images={images}
        initialIndex={selectedImageIndex}
      />

      {/* Transaction Fee Modal */}
      <Modal show={showTransactionFeeModal} onHide={() => setShowTransactionFeeModal(false)} centered>
        <Modal.Header closeButton style={{ border: 'none' }} />
        <Modal.Body style={{ paddingTop:20, paddingBottom:20, paddingRight:20, paddingLeft:20, textAlign: 'center' }}>
          <div style={{ background: '#F0F5FA', borderRadius: '12px', padding: '32px 24px' }}>
            <h4 style={{ fontWeight: 700, fontSize: '24px', marginBottom: '12px', color: '#000' }}>
              {transactionFeeData?.buyerPremiumPercentage ? `${transactionFeeData.buyerPremiumPercentage}%` : buyerAgentCommissionText && buyerAgentCommissionText !== 'No' ? buyerAgentCommissionText : ''} Transaction Fee
            </h4>
            <p style={{ color: '#48525B', fontSize: '16px', fontWeight: 400, marginBottom: '24px' }}>
              Fee based on final selected bid amount<br />and paid by the buyer at closing.
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', marginBottom: '8px' }}>
              <span style={{ color: '#48525B' }}>Minimum Transaction Fee</span>
              <span style={{ color: '#000', fontWeight: 600 }}>
                {transactionFeeData?.buyerPremiumFormatted || (transactionFeeData?.buyerPremiumAmount ? `$${getFormatPrice(transactionFeeData.buyerPremiumAmount)}` : '—')}
              </span>
            </div>
            <h5 style={{ fontWeight: 600, fontSize: '18px', margin: '20px 0 12px', textAlign: 'center', color: '#000' }}>
              Example Transaction Fee
            </h5>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', marginBottom: '8px' }}>
              <span style={{ fontWeight: 400, color: '#48525B' }}>Final Selected Bid Amount</span>
              <span style={{ color: '#000', fontWeight: 600 }}>
                $100,000
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', marginBottom: '8px' }}>
              <span style={{ fontWeight: 400, color: '#48525B' }}>Transaction Fee</span>
              <span style={{ color: '#000', fontWeight: 600 }}>
                {transactionFeeData?.buyerPremiumFormatted || (transactionFeeData?.buyerPremiumAmount ? `$${getFormatPrice(transactionFeeData.buyerPremiumAmount)}` : '—')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', marginTop: '12px', paddingTop: '12px' }}>
              <span style={{ fontWeight: 800, color: '#48525B' }}>Total Purchase Price</span>
              <span style={{ fontWeight: 800, color: '#000' }}>
                {transactionFeeData?.buyerPremiumAmount ? `$${getFormatPrice(100000 + Number(transactionFeeData.buyerPremiumAmount))}` : '—'}
              </span>
            </div>
            <p style={{ color: '#48525B', fontSize: '13px', fontWeight: 400, marginTop: '20px', textAlign: 'center', lineHeight: '1.6' }}>
              This sample calculation is for illustrative purposes only and may not include all closing-related fees. It may not reflect your final selected bid amount. For more information about the transaction fee, please review the Fees and Disclaimers on this property detail page.
            </p>
          </div>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default CommercialHeroSection;
